import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Data, Params, Router } from '@angular/router';
import { RouterReducerState } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { SitemapEntry } from '@wdx/clmi/clmi-swagger-gen';
import { CrudStatus } from '@wdx/shared/utils';
import {
    combineLatest,
    filter,
    map,
    Observable,
    pairwise,
    startWith,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { RouterStateAndUrl } from './custom-route-serializer';
import { refreshExtendedRouteState } from './extended-route.actions';
import {
    selectExtendedIsAdminArea,
    selectExtendedParentSitemapEntries,
    selectExtendedRouteStatus,
    selectExtendedSitemapEntry,
    selectExtendedWholeSitemap,
    selectIsExtendedRoutePending,
} from './extended-route.selectors';
import {
    selectPath,
    selectQueryParam,
    selectQueryParams,
    selectRouteData,
    selectRouteDataParam,
    selectRouteParam,
    selectRouteParams,
    selectRouterState,
    selectUrl,
} from './router.selector';

export interface RouteIds {
    activityId?: string;
    appId?: string;
    campaignId?: string;
    caseId?: string;
    caseTypeCode?: string;
    dashboardId?: string;
    entityId?: string;
    entityType?: string;
    eventId?: string;
    formId?: string;
    importId?: string;
    listId?: string;
    messageBatchId?: string;
    productId?: string;
    teamId?: string;
    workItemId?: string;
}

@Injectable({ providedIn: 'root' })
export class RouteFacade {
    private router = inject(Router);
    protected route = inject(ActivatedRoute);

    private store = inject(Store) as Store<{
        router: RouterReducerState<RouterStateAndUrl>;
        extendedRoute: any;
    }>;

    getRouteState$(): Observable<RouterStateAndUrl> {
        return this.store.select(selectRouterState);
    }

    getRouteParams$(): Observable<Params> {
        return this.store.select(selectRouteParams);
    }

    getRouteParamField$(field: string): Observable<string | undefined> {
        return this.store.select(selectRouteParam(field));
    }

    getRouteIds$(): Observable<RouteIds> {
        return this.store.select(selectRouteParams).pipe(
            map((params) => {
                const {
                    activityId,
                    appId,
                    campaignId,
                    caseId,
                    caseTypeCode,
                    dashboardId,
                    entityId,
                    entityType,
                    eventId,
                    formId,
                    importId,
                    listId,
                    messageBatchId,
                    productId,
                    teamId,
                    workItemId,
                    stageWorkItemId,
                } = params;

                return {
                    ...(activityId ? { activityId } : {}),
                    ...(appId ? { appId } : {}),
                    ...(campaignId ? { campaignId } : {}),
                    ...(caseId ? { caseId } : {}),
                    ...(caseTypeCode ? { caseTypeCode } : {}),
                    ...(dashboardId ? { dashboardId } : {}),
                    ...(entityId ? { entityId } : {}),
                    ...(entityType ? { entityType } : {}),
                    ...(eventId ? { eventId } : {}),
                    ...(formId ? { formId } : {}),
                    ...(importId ? { importId } : {}),
                    ...(listId ? { listId } : {}),
                    ...(messageBatchId ? { messageBatchId } : {}),
                    ...(productId ? { productId } : {}),
                    ...(teamId ? { teamId } : {}),
                    ...(workItemId || stageWorkItemId
                        ? { workItemId: workItemId || stageWorkItemId }
                        : {}),
                };
            }),
        );
    }

    getRouteData$(): Observable<Data> {
        return this.store.select(selectRouteData);
    }

    getRouteDataField$<T>(field: string): Observable<T> {
        return this.store.select(selectRouteDataParam(field)) as Observable<T>;
    }

    getUrl$(): Observable<string> {
        return this.store.select(selectUrl);
    }

    getQueryParams$(): Observable<Params> {
        return this.store.select(selectQueryParams);
    }

    getQueryParam$(field: string): Observable<string | undefined> {
        return this.store.select(selectQueryParam(field));
    }

    getPath$(): Observable<string[]> {
        return this.store.select(selectPath).pipe(
            startWith([]),
            pairwise(),
            filter(
                ([prev, current]) =>
                    JSON.stringify(prev) !== JSON.stringify(current) &&
                    Boolean(current),
            ),
            map(([_, current]) => current),
        );
    }

    updateQueryParam(updatedQueryParams: Record<string, string>): void {
        this.getQueryParams$()
            .pipe(take(1))
            .subscribe((queryParams) => {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: {
                        ...queryParams,
                        ...updatedQueryParams,
                    },
                    queryParamsHandling: 'merge',
                });
            });
    }

    /**
     * Gets the current sitemap entry for the active route.
     * Returns the leaf sitemap entry (the deepest matched entry).
     * This comes from the extended route state, not the standard router state.
     */
    getSitemapEntry$(): Observable<SitemapEntry | undefined> {
        return this.store.select(selectExtendedSitemapEntry);
    }

    /**
     * Gets parent sitemap entries for the active route.
     * Returns an array of parent sitemap entries (from root up to, but not including, the current route).
     * This comes from the extended route state, not the standard router state.
     */
    getParentSitemapEntries$(): Observable<SitemapEntry[] | undefined> {
        return this.store.select(selectExtendedParentSitemapEntries);
    }

    /**
     * Gets the full sitemap for the current area (admin or regular).
     * This comes from the extended route state, not the standard router state.
     */
    getWholeSitemap$(): Observable<SitemapEntry[] | undefined> {
        return this.store.select(selectExtendedWholeSitemap);
    }

    /**
     * Checks if the current route is in the admin area.
     * This comes from the extended route state, not the standard router state.
     */
    getIsAdminArea$(): Observable<boolean | undefined> {
        return this.store.select(selectExtendedIsAdminArea);
    }

    /**
     * Gets the status of the extended route state.
     */
    getExtendedRouteStatus$(): Observable<CrudStatus> {
        return this.store.select(selectExtendedRouteStatus);
    }

    /**
     * Checks if the extended route state is pending (waiting for sitemap to load).
     */
    getIsExtendedRoutePending$(): Observable<boolean> {
        return this.store.select(selectIsExtendedRoutePending);
    }

    /**
     * Refreshes the extended route state by re-computing it from the current router state.
     * Use this when you need to refresh the extended route state, for example when
     * the sitemap has loaded after navigation completed.
     */
    refreshExtendedRouteState(): void {
        this.store.dispatch(refreshExtendedRouteState());
    }

    /**
     * Gets the combined route state (standard router state + extended route state).
     * This provides a single observable that combines both states without duplication.
     *
     * If the extended route state hasn't been computed yet (status is Initial) or
     * is waiting for sitemap to load (status is Loading), this will automatically
     * trigger a refresh to ensure the extended state is populated.
     */
    getCombinedRouteState$(): Observable<
        RouterStateAndUrl & {
            sitemapEntry?: SitemapEntry;
            parentSitemapEntries?: SitemapEntry[];
            wholeSitemap?: SitemapEntry[];
            isAdminArea?: boolean;
        }
    > {
        // Check status and route state once when subscribed, and trigger refresh if needed
        return combineLatest([
            this.store.select(selectExtendedRouteStatus),
            this.store.select(selectRouterState),
        ]).pipe(
            take(1),
            tap(([status, routerState]) => {
                // Only trigger refresh if:
                // 1. Status indicates we need to compute/refresh (Initial or Pending)
                //    - Initial: not computed yet
                //    - Pending: waiting for sitemap to load
                // 2. We have a valid route state (path exists and has length > 0)
                // This prevents infinite loops when there's no route or sitemap
                if (
                    (status === CrudStatus.Initial ||
                        status === CrudStatus.Pending) &&
                    routerState &&
                    routerState.path &&
                    routerState.path.length > 0
                ) {
                    this.refreshExtendedRouteState();
                }
            }),
            switchMap(() =>
                combineLatest([
                    this.store.select(selectRouterState),
                    this.store.select(selectExtendedSitemapEntry),
                    this.store.select(selectExtendedParentSitemapEntries),
                    this.store.select(selectExtendedWholeSitemap),
                    this.store.select(selectExtendedIsAdminArea),
                ]).pipe(
                    map(
                        ([
                            routerState,
                            sitemapEntry,
                            parentSitemapEntries,
                            wholeSitemap,
                            isAdminArea,
                        ]) => {
                            if (!routerState) {
                                return undefined as any;
                            }
                            return {
                                ...routerState,
                                ...(sitemapEntry ? { sitemapEntry } : {}),
                                ...(parentSitemapEntries
                                    ? { parentSitemapEntries }
                                    : {}),
                                ...(wholeSitemap ? { wholeSitemap } : {}),
                                ...(isAdminArea !== undefined
                                    ? { isAdminArea }
                                    : {}),
                            };
                        },
                    ),
                    filter(
                        (state): state is NonNullable<typeof state> =>
                            state !== undefined,
                    ),
                ),
            ),
        );
    }
}
